import type React from "react"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, AlertCircle, Upload, X, LogOut, CheckSquare, Square } from "lucide-react"
import { useArtJob } from "@/hooks/useArtJob"
import { useLuckyStream } from "@/hooks/useLuckyStream"
import { apiClient } from "@/lib/api-client"
import { useToast } from "@/hooks/use-toast"

export default function GenerationPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [prompt, setPrompt] = useState("")
  const [photos, setPhotos] = useState<File[]>([])
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isGeneratingKVDescription, setIsGeneratingKVDescription] = useState(false)
  
  // 🎨 NOVA: Seleção de resolução
  const [width, setWidth] = useState(1080)
  const [height, setHeight] = useState(1080)
  const [selectedPreset, setSelectedPreset] = useState<string>("Instagram Feed")
  
  // 🖼️ MODO KV (Key Visual)
  const [modoKV, setModoKV] = useState(false)
  const [keyVisualFile, setKeyVisualFile] = useState<File | null>(null)
  const [keyVisualPreview, setKeyVisualPreview] = useState<string>("")
  const [kvDescription, setKvDescription] = useState<string>("")

  // 🍀 MODO LUCKY
  const [modoLucky, setModoLucky] = useState(false)
  const [availableVariations, setAvailableVariations] = useState<string[]>([])
  const [selectedVariations, setSelectedVariations] = useState<string[]>([])
  const [loadingVariations, setLoadingVariations] = useState(false)

  const { createArtJob, pollArtJob } = useArtJob()
  const {
    isStreaming: isLuckyStreaming,
    progress: luckyProgress,
    completedVariations,
    totalVariations,
    currentVariation,
    startLuckyStream
  } = useLuckyStream()

  // Função helper para converter nome de variação para nome amigável
  const getVariationDisplayName = (variationName: string): string => {
    // Remove prefixo "MASCOTE LUCKY - " se existir
    let displayName = variationName.replace("MASCOTE LUCKY - ", "").trim()
    // Converte primeira letra para maiúscula e resto para minúscula
    if (displayName.length > 0) {
      displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1).toLowerCase()
    }
    return displayName
  }

  // Presets de resolução por plataforma
  const resolutionPresets = [
    { id: "instagram-feed", label: "Instagram Feed (1:1)", width: 1080, height: 1080, ratio: "1:1" },
    { id: "instagram-portrait", label: "Instagram Retrato (4:5)", width: 1080, height: 1350, ratio: "4:5" },
    { id: "instagram-stories", label: "Instagram Stories (9:16)", width: 1080, height: 1920, ratio: "9:16" },
    { id: "tiktok", label: "TikTok (9:16)", width: 1080, height: 1920, ratio: "9:16" },
    { id: "youtube-shorts", label: "YouTube Shorts (9:16)", width: 1080, height: 1920, ratio: "9:16" },
    { id: "youtube-standard", label: "YouTube (16:9)", width: 1920, height: 1080, ratio: "16:9" },
    { id: "facebook-stories", label: "Facebook Stories (9:16)", width: 1080, height: 1920, ratio: "9:16" },
    { id: "twitter", label: "Twitter/X (16:9)", width: 1600, height: 900, ratio: "16:9" },
  ]

  const handlePresetSelect = (presetId: string) => {
    const preset = resolutionPresets.find(p => p.id === presetId)
    if (preset) {
      setWidth(preset.width)
      setHeight(preset.height)
      setSelectedPreset(presetId)
    }
  }

  const handleKeyVisualChange = (files: FileList | null) => {
    if (!files) return
    const file = files[0]
    if (file) {
      // Se ligar KV, desliga Sonho da Sorte
      if (!modoKV) {
        // setModoSonhoDaSorte(false) // Removido
      }
      setKeyVisualFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setKeyVisualPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
      
      // 🎯 Gerar descrição automática do KV
      generateKVDescription(file).then((description) => {
        if (description) {
          setKvDescription(description)
          console.log('[KV] Descrição salva no estado:', description)
        }
      })
    }
  }

  // 💜 Handler para upload de foto de Sonho da Sorte
  // const handleSonhoPhotoChange = (files: FileList | null) => { // Removido
  //   if (!files) return
  //   const file = files[0]
  //   if (file) {
  //     // Se ligar Sonho da Sorte, desliga KV
  //     if (!modoSonhoDaSorte) {
  //       setModoKV(false)
  //       setKeyVisualFile(null)
  //       setKeyVisualPreview("")
  //     }
  //     setSonhoPhoto(file)
  //     const reader = new FileReader()
  //     reader.onload = (e) => {
  //       setSonhoPhotoPreview(e.target?.result as string)
  //     }
  //     reader.readAsDataURL(file)
  //     console.log('[SONHO DA SORTE] Foto de Sonho da Sorte carregada:', file.name)
  //   }
  // }

  // 🎯 Função para gerar descrição do KV usando Gemini Vision API
  const generateKVDescription = async (file: File): Promise<string> => {
    try {
      setIsGeneratingKVDescription(true)
      console.log('[KV] Gerando descrição automática do Key Visual...')
      
      // Converter arquivo para base64
      const reader = new FileReader()
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      
      const base64Image = await base64Promise
      console.log('[KV] Imagem convertida para base64, tamanho:', base64Image.length)
      
      // Chamar API do backend usando apiClient (segue padrão existente)
      const response = await apiClient.describeImage(base64Image, 'key_visual')
      
      console.log('[KV] Response status: 200')
      const description = response.data?.description || ""
      console.log('[KV] Descrição gerada:', description)
      return description
    } catch (error) {
      console.error('[KV] Erro ao gerar descrição:', error)
      console.error('[KV] Stack:', error instanceof Error ? error.stack : 'N/A')
      
      // Mostrar erro mais específico
      const errorMsg = error instanceof Error ? error.message : String(error)
      toast({
        title: "Aviso",
        description: `Descrição do KV: usando padrão. Erro: ${errorMsg}`,
        variant: "default",
      })
      
      // Retornar descrição genérica se falhar
      return "Imagem Key Visual para ser usada como referência principal na composição"
    } finally {
      setIsGeneratingKVDescription(false)
    }
  }

  // Buscar variações quando modo Lucky for ativado
  useEffect(() => {
    const fetchVariations = async () => {
      if (modoLucky && availableVariations.length === 0) {
        setLoadingVariations(true)
        try {
          const response = await apiClient.listLuckyVariations()
          setAvailableVariations(response.data.variations)
          // Por padrão, seleciona todas as variações
          setSelectedVariations(response.data.variations)
        } catch (error: any) {
          console.error('[LUCKY] Erro ao buscar variações:', error)
          toast({
            title: "Erro",
            description: "Não foi possível carregar as variações do Lucky",
            variant: "destructive",
          })
        } finally {
          setLoadingVariations(false)
        }
      }
    }

    fetchVariations()
  }, [modoLucky, availableVariations.length, toast])

  // Verifica autenticação ao montar o componente
  useEffect(() => {
    const checkAuth = () => {
      if (!apiClient.isAuthenticated()) {
        navigate("/login")
        return
      }
      setIsLoading(false)
      
      // Mostrar toast de boas-vindas apenas uma única vez após login
      const userEmail = localStorage.getItem('userEmail')
      if (userEmail) {
        // Em mobile, usar duration curta (2s) para auto-dismiss
        const isMobile = window.innerWidth < 640
        toast({
          title: "Bem-vindo!",
          description: `Logado como ${userEmail}`,
          duration: isMobile ? 2000 : 3000,
        })
        // Remover o email após exibir para não repetir
        localStorage.removeItem('userEmail')
      }
    }

    checkAuth()
  }, [navigate, toast])

  const handleLogout = async () => {
    try {
      await apiClient.logout()
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso",
      })
      navigate("/login")
    } catch (error) {
      console.error("Erro ao fazer logout:", error)
      toast({
        title: "Erro",
        description: "Erro ao fazer logout",
        variant: "destructive",
      })
    }
  }

  const handlePhotosChange = (files: FileList | null) => {
    if (!files) return

    const newPhotos = Array.from(files).slice(0, 2 - photos.length)
    const updatedPhotos = [...photos, ...newPhotos].slice(0, 2)
    setPhotos(updatedPhotos)

    // Reconstrói o array de previews a partir do zero para evitar desincronização
    const newPreviews: string[] = []
    let loadedCount = 0

    updatedPhotos.forEach((file, index) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        newPreviews[index] = reader.result as string
        loadedCount++
        // Atualiza o estado apenas quando todas as fotos foram processadas
        if (loadedCount === updatedPhotos.length) {
          setPhotoPreviews(newPreviews)
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const removePhoto = (index: number) => {
    console.log(`[DEBUG] Removendo foto no índice ${index}`)
    setPhotos((prev) => {
      const updated = prev.filter((_, i) => i !== index)
      console.log(`[DEBUG] Fotos após remover:`, updated)
      return updated
    })
    setPhotoPreviews((prev) => {
      const updated = prev.filter((_, i) => i !== index)
      console.log(`[DEBUG] Previews após remover:`, updated)
      return updated
    })
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    handlePhotosChange(files)
  }

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      let finalPrompt = prompt

      // 💜 MODO SONHO DA SORTE (INDEPENDENTE - ignora tudo mais)
      // if (modoSonhoDaSorte && sonhoPhoto) { // Removido
      //   console.log('[SONHO DA SORTE] 🎯 Gerando recolor roxo')
      //   
      //   // Prompt para recolor roxo - foco em fundo roxo
      //   let sonhoPrompt = `Recolore a imagem para a identidade visual Sonho da Sorte. 
      // INSTRUÇÕES CRÍTICAS:
      // - Fundo: deve ser ROXO (#7B4FB0) ou tons de roxo
      // - Cores principais: tender para roxo e amarelo (#FFD700)
      // - Mantenha o sujeito principal (pessoa/animal/objeto) reconhecível
      // - Aplique roxo em fundo, sombras, destaques secundários
      // - O resultado final deve TER MUITO ROXO como cor dominante`
      //   
      //   // APPEND: adicionar prompt do usuário se fornecido
      //   if (prompt.trim()) {
      //     sonhoPrompt += `\n\nADICIONALMENTE: ${prompt}`
      //     console.log('[SONHO DA SORTE] Append do usuário:', prompt)
      //   }
      //   
      //   finalPrompt = sonhoPrompt
      //   
      //   // Foto do sonho como input ÚNICO
      //   const finalPhotos = [sonhoPhoto]
      //   
      //   console.log('[SONHO DA SORTE] Prompt final:', finalPrompt)
      //   console.log('[SONHO DA SORTE] Iniciando geração...')
      //   
      //   toast({
      //     title: "Recolorindo...",
      //     description: "Aplicando branding roxo Sonho da Sorte",
      //   })
      //   
      //   // Criar job
      //   const job = await createArtJob(
      //     finalPrompt,
      //     finalPhotos,
      //     undefined, // seed
      //     width,
      //     height,
      //     'generate',
      //     undefined,
      //     false, // modoKV
      //     false  // modo_kv_branding
      //   )
      //   
      //   if (!job) {
      //     throw new Error('Falha ao criar job de recolor')
      //   }
      //   
      //   // Polling
      //   const completedJob = await pollArtJob(job.id)
      //   
      //   if (completedJob && completedJob.status === 'succeeded') {
      //     console.log('[SONHO DA SORTE] ✅ Recolor gerado com sucesso!')
      //     navigate(`/result/${completedJob.id}`)
      //   }
      //   return // Sair aqui - não continua com lógica normal
      // }

      // LÓGICA NORMAL (não é Sonho da Sorte)
      // Validar Modo Sonho da Sorte separadamente
      // if (modoSonhoDaSorte) { // Removido
      //   // Modo Sonho da Sorte é INDEPENDENTE - apenas precisa da foto
      //   if (!sonhoPhoto) {
      //     toast({
      //       title: "Erro",
      //       description: "Modo Sonho da Sorte requer 1 foto",
      //       variant: "destructive",
      //     })
      //     return
      //   }
      //   
      //   console.log('[SONHO DA SORTE] 🎯 Gerando recolor roxo/amarelo')
      //   // Continua com lógica de Sonho da Sorte abaixo
      // } else {
        // Validar modos normais: precisa de prompt OU (1+ fotos) OU (KV + foto referência)
        const hasKVWithReference = modoKV && keyVisualFile && photos.length > 0
        const hasPhotosWithoutKV = photos.length > 0 && !modoKV
        const hasPrompt = prompt.trim().length > 0
        
        if (!hasPrompt && !hasPhotosWithoutKV && !hasKVWithReference && !modoKV) {
          toast({
            title: "Erro",
            description: "Por favor: insira um prompt, OU adicione foto(s) de referência, OU use KV + foto de referência",
            variant: "destructive",
          })
          return
        }
      // }

      // 🎯 LÓGICA COM PESO MAIOR PARA KV
      if (modoKV && keyVisualFile && kvDescription) {
        // Base fixa que dá peso ao KV
        const kvBase = "Este é a descrição do Key Visual: quero que ele seja a base e a parte maior da arte apenas seguindo se houver alguma modificação dita em prompt"
        
        // Caso 1: KV apenas (sem fotos de referência e sem prompt)
        if (photos.length === 0 && !prompt.trim()) {
          finalPrompt = `${kvBase}\n\nDescrição do KV: ${kvDescription}\n\nINSTRUÇÕES: Ignore qualquer UI elements, text overlays, assets decorativos ou written content. Foque apenas na composição, pose, iluminação e contexto geral do Key Visual.`
          console.log('[KV] Caso 1 - KV apenas')
        }
        // Caso 2: KV + fotos de referência (sem prompt) - interpolação com peso no KV
        else if (photos.length > 0 && !prompt.trim()) {
          const referencePhrase = `Você tem DUAS imagens:
1. KEY VISUAL (principal - MAIOR IMPORTÂNCIA): ${kvDescription}
2. Imagem de referência (complemento): para agregar elementos

MUITO IMPORTANTE: O Key Visual DEVE ser a base e ocupar a MAIOR PARTE da arte! 
Use a imagem de referência apenas para AGREGAR elementos de forma lógica, harmoniosa e criativa.

Crie uma JUNÇÃO CRIATIVA onde o Key Visual é predominante, mas que incorpore ideias interessantes da referência de forma natural.

INSTRUÇÕES CRÍTICAS:
- Ignore qualquer UI elements, text overlays, written assets, ou decorative items nas imagens, ignorar qualquer texto, letreiros, ou assets decorativos
- Foque APENAS no contexto geral, composição, pose e elementos visuais principais
- Retire/ignore qualquer texto, letreiros, ou assets decorativos
- Mantenha a pose, body language e spatial composition dos personagens
- Priorize: composição, iluminação, pose e contexto geral
- Ignore: UI elements, textos, e assets decorativos
- RETORNE UMA FOTO`
          finalPrompt = `${kvBase}\n\n${referencePhrase}`
          console.log('[KV] Caso 2 - KV + referência (sem prompt)')
        }
        // Caso 3: KV + prompt fornecido - KV como base, siga as instruções do prompt
        else if (prompt.trim()) {
          finalPrompt = `${kvBase}\n\nDescrição do KV: ${kvDescription}\n\nModificações solicitadas: ${prompt}`
          console.log('[KV] Caso 3 - KV + prompt')
        }
      }
      // Caso 4: Sem KV, com 1+ fotos, sem prompt - gerar variação/interpolação
      else if (photos.length > 0 && !prompt.trim() && !modoKV) {
        if (photos.length === 1) {
          finalPrompt = "Variação desta foto, me dê uma foto diferente, uma variação"
          console.log('[DEBUG] 🎨 Variação simples sem prompt')
        } else {
          // Múltiplas fotos sem prompt - interpolar
          finalPrompt = `Você tem ${photos.length} imagens de referência. Crie uma composição/interpolação criativa que combina elementos interessantes de TODAS as imagens de forma harmoniosa e natural. A arte final deve ser uma síntese criativa que respeita o estilo e contexto de todas as referências.`
          console.log(`[DEBUG] 🎨 Interpolação de ${photos.length} fotos sem prompt`)
        }
      }

      console.log('[DEBUG] Iniciando geração de arte...')
      console.log('[DEBUG] Prompt final:', finalPrompt)
      console.log('[DEBUG] Fotos:', photos.length)
      console.log('[DEBUG] Resolução:', `${width}x${height}`)
      console.log('[DEBUG] MODO KV:', modoKV)
      console.log('[DEBUG] KV Description:', kvDescription)
      
      // 🍀 MODO LUCKY: Usa SSE para progresso em tempo real
      if (modoLucky) {
        console.log('[DEBUG] 🍀 Usando SSE para modo Lucky...')

        toast({
          title: "Iniciando Lucky Mode",
          description: `Gerando ${selectedVariations.length} variações...`,
        })

        const jobId = await startLuckyStream({
          prompt: finalPrompt,
          width,
          height,
          selected_variations: selectedVariations,
        })

        if (jobId) {
          console.log('[DEBUG] ✅ Lucky mode concluído! Job ID:', jobId)
          navigate(`/result/${jobId}`)
        } else {
          throw new Error('Falha ao gerar imagens no modo Lucky')
        }
        return
      }

      // MODO NORMAL (não-Lucky): usa polling
      // Show loading toast
      toast({
        title: "Enviando fotos...",
        description: photos.length > 0 ? "Isso pode demorar alguns segundos com fotos grandes" : "Gerando arte...",
      })

      // Preparar fotos (adicionar Key Visual como primeira foto se ativo)
      let finalPhotos = [...photos]

      // 🚨 LÓGICA: Adicionar keyVisualFile para KV (SEMPRE)
      if (modoKV && keyVisualFile) {
        finalPhotos.unshift(keyVisualFile)
        console.log('[DEBUG] Key Visual adicionado como primeira foto')
      }

      // 1. Criar job de arte com Gemini (com fotos opcionais e resolução)
      const job = await createArtJob(
        finalPrompt,
        finalPhotos,
        undefined, // seed
        width,     // resolver normal (preset selecionado)
        height,    // resolver normal (preset selecionado)
        'generate', // mode
        undefined,  // imageUrl
        modoKV,      // modo_kv
        false,       // REMOVIDO: modoKVBranding removido
        false,       // modo_lucky (sempre false aqui, lucky usa SSE acima)
        undefined    // selected_variations
      )

      console.log('[DEBUG] Job criado:', job)

      if (!job) {
        const errorMsg = 'Falha ao criar job de arte. Por favor, tente novamente.'
        console.error('[DEBUG] ❌', errorMsg)
        toast({
          title: "Erro",
          description: errorMsg,
          variant: "destructive",
        })
        throw new Error(errorMsg)
      }

      console.log('[DEBUG] ✅ Job criado com sucesso, iniciando polling...')

      // 2. Polling até job completar
      const completedJob = await pollArtJob(job.id)

      if (completedJob && completedJob.status === 'succeeded') {
        console.log('[DEBUG] ✅ Arte gerada com sucesso!')
        navigate(`/result/${completedJob.id}`)
      }
    } catch (error: any) {
      console.error("[DEBUG] ❌ Generation failed:", error)
      console.error("[DEBUG] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      })
      
      const errorMessage = error.response?.data?.detail || error.message || 'Erro desconhecido ao gerar arte'
      toast({
        title: "Erro ao gerar arte",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const isFormValid = prompt.trim().length > 0

  // Permite gerar se: tem prompt OU (1 foto sem KV) OU (KV + referência) OU (Lucky com variações selecionadas)
  const canGenerate = prompt.trim().length > 0 || 
    (photos.length === 1 && !modoKV && !modoLucky && !keyVisualFile) ||
    (modoKV && keyVisualFile && photos.length > 0) ||
    (modoLucky && selectedVariations.length > 0) ||
    false

  if (isLoading) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-300 text-sm">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="w-full flex flex-col items-center py-4 sm:py-6 px-2 sm:px-6 min-h-screen"
    >
      {/* Botão de Logout no canto superior direito */}
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50">
        <Button
          onClick={handleLogout}
          variant="outline"
          className="flex items-center gap-2 border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-lg sm:text-sm px-5 py-3"
        >
          <LogOut className="h-6 w-6" />
          <span className="hidden sm:inline">Sair</span>
        </Button>
      </div>

      <Card className="w-full max-w-3xl p-6 sm:p-10 shadow-2xl border-purple-500/20 bg-gradient-to-br from-slate-900/95 to-purple-900/40 backdrop-blur-sm mt-12 sm:mt-0">
        <div className="space-y-8">
          {/* Header with Orbiting Glow on Text */}
          <div className="text-center space-y-4">
            <style>{`
              @keyframes textOrbitingGlow {
                0% { background-position: 0% 50%; }
                25% { background-position: 100% 0%; }
                50% { background-position: 100% 100%; }
                75% { background-position: 0% 100%; }
                100% { background-position: 0% 50%; }
              }
              
              .glowing-title {
                font-size: clamp(2.5rem, 10vw, 2.5rem);
                font-weight: 700;
                letter-spacing: -0.025em;
                background: linear-gradient(45deg, rgba(168, 85, 247, 0) 0%, rgba(168, 85, 247, 0.8) 20%, rgba(147, 51, 234, 1) 50%, rgba(168, 85, 247, 0.8) 80%, rgba(168, 85, 247, 0) 100%);
                background-size: 200% 200%;
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: textOrbitingGlow 4s ease-in-out infinite;
              }
            `}</style>
            
            <h1 className="glowing-title">Gerador de Arte</h1>
            <p className="text-purple-100 text-lg sm:text-xl">Descreva a arte que você deseja gerar com IA ou adicione uma foto para criar variações</p>
          </div>

          {/* Prompt Section */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <Label htmlFor="prompt" className="text-lg sm:text-lg font-semibold text-purple-50">
                Prompt de Descrição {photos.length === 0 || modoKV ? '*' : '(Opcional)'}
              </Label>
            </div>
            <Textarea
              id="prompt"
              placeholder={photos.length === 1 && !modoKV ? "Deixe em branco para gerar variação automática, ou descreva como quer a variação..." : "Ex: Uma montanha nevada ao amanhecer com cores vibrantes, estilo digital art..."}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="resize-none border-purple-500/40 bg-purple-950/40 text-purple-50 placeholder:text-purple-300/40 focus:border-purple-500/60 focus:shadow-lg focus:shadow-purple-500/20 transition-all text-lg sm:text-base p-4"
            />
            <p className="text-base text-purple-200/70">
              {photos.length === 1 && !modoKV 
                ? "💡 Dica: Deixe em branco para criar uma variação automática da foto ou descreva como quer que mude"
                : "Descreva detalhadamente o estilo, cores, composição e elementos que deseja na sua arte."}
            </p>
          </div>

          {/* 🎨 Resolução */}
          <div className="space-y-4 border-t border-purple-500/20 pt-6">
            <Label className="text-base sm:text-lg font-semibold text-purple-50">Resolução da Imagem</Label>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {resolutionPresets.map((preset) => (
                <Button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset.id)}
                  className={`text-xs sm:text-sm font-medium transition-all py-3 px-2 h-auto min-h-12 ${
                    selectedPreset === preset.id
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-600/50 hover:shadow-xl hover:shadow-purple-600/70"
                      : "bg-white text-black border border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-50 hover:shadow-lg hover:shadow-purple-500/20"
                  }`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            <div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-3">
              <div>
                <Label htmlFor="width" className="text-sm sm:text-base text-purple-50 font-semibold block mb-2">Largura (px)</Label>
                <input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(Number(e.target.value))}
                  min="256"
                  max="2048"
                  step="64"
                  className="w-full px-4 py-3 text-base sm:text-sm border rounded-md border-purple-500/40 bg-purple-950/40 text-purple-50 placeholder:text-purple-300/40 focus:border-purple-500/60 focus:shadow-lg focus:shadow-purple-500/20 transition-all h-12"
                />
              </div>
              <div>
                <Label htmlFor="height" className="text-sm sm:text-base text-purple-50 font-semibold block mb-2">Altura (px)</Label>
                <input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(Number(e.target.value))}
                  min="256"
                  max="2048"
                  step="64"
                  className="w-full px-4 py-3 text-base sm:text-sm border rounded-md border-purple-500/40 bg-purple-950/40 text-purple-50 placeholder:text-purple-300/40 focus:border-purple-500/60 focus:shadow-lg focus:shadow-purple-500/20 transition-all h-12"
                />
              </div>
            </div>

            <p className="text-sm sm:text-base text-purple-200/70">
              Resolução atual: <strong>{width}×{height}px</strong>
            </p>
          </div>

          {/* Photo Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base sm:text-lg font-semibold text-purple-50">
                Fotos de Referência {modoLucky ? "(Desabilitado no Modo Lucky)" : "(Opcional)"}
              </Label>
              <span className="text-sm sm:text-base text-purple-200/70">{photos.length}/2 fotos</span>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-6 sm:p-8 transition-all ${
                modoLucky 
                  ? "border-purple-500/20 bg-purple-900/20 cursor-not-allowed opacity-50"
                  : dragActive
                  ? "border-purple-400 bg-purple-600/20 shadow-lg shadow-purple-500/30 cursor-pointer"
                  : "border-purple-500/50 bg-purple-900/40 hover:border-purple-500/80 hover:bg-purple-900/60 shadow-md shadow-purple-500/20 cursor-pointer"
              }`}
            >
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handlePhotosChange(e.target.files)}
                disabled={photos.length >= 2 || modoLucky}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />

              <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4 text-center">
                <div className="rounded-full bg-purple-500/40 p-4 sm:p-5">
                  <Upload className="h-8 w-8 sm:h-10 sm:w-10 text-purple-300" />
                </div>
                <div>
                  <p className="font-semibold text-purple-50 text-base sm:text-lg">
                    {photos.length >= 2 ? "Máximo de 2 fotos atingido" : "Arraste fotos aqui ou clique para selecionar"}
                  </p>
                  <p className="text-sm sm:text-base text-purple-200 mt-2">PNG, JPG ou WebP (até 10MB cada)</p>
                </div>
              </div>
            </div>

            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative group rounded-lg overflow-hidden border border-purple-500/30">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        removePhoto(index)
                      }}
                      className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 rounded-full p-3 transition-all cursor-pointer z-10 shadow-lg hover:shadow-xl"
                      title="Remover foto"
                      aria-label="Remover foto"
                    >
                      <X className="h-6 w-6 text-white" />
                    </button>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MODO KV Toggle */}
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center justify-between">
              <Label className="text-lg sm:text-lg font-semibold text-purple-50">🖼️ Modo Key Visual</Label>
              <button
                onClick={() => {
                  if (!modoKV) {
                    setModoLucky(false) // Desligar modo Lucky se ativar KV
                  }
                  setModoKV(!modoKV)
                }}
                className={`w-16 h-8 rounded-full transition-all ${
                  modoKV ? "bg-purple-600" : "bg-gray-400"
                }`}
              >
                <div className={`w-7 h-7 bg-white rounded-full transition-transform ${
                  modoKV ? "translate-x-8" : "translate-x-0.5"
                }`} />
              </button>
            </div>
            {modoKV && (
              <div className="space-y-4 p-5 border-2 rounded-lg bg-purple-900/40 border-purple-500/50 shadow-md shadow-purple-500/20">
                <Label className="text-base sm:text-base font-medium text-purple-50">Upload do Key Visual (obrigatório no Modo KV)</Label>
                <div className="relative border-2 border-dashed rounded-lg p-8 transition-all cursor-pointer border-purple-500/60 bg-purple-950/50 hover:border-purple-400 hover:bg-purple-900/60">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleKeyVisualChange(e.target.files)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  <div className="flex flex-col items-center justify-center space-y-4 text-center">
                    <div className="rounded-full bg-purple-500/40 p-4">
                      <Upload className="h-8 w-8 text-purple-300" />
                    </div>
                    <div>
                      <p className="font-medium text-purple-50 text-lg">Clique ou arraste uma imagem</p>
                      <p className="text-base text-purple-200">PNG, JPG ou WebP</p>
                    </div>
                  </div>
                </div>
                {keyVisualPreview && (
                  <div className="relative w-full h-48 rounded-md overflow-hidden border-2 border-purple-500/40 shadow-md">
                    <img src={keyVisualPreview} alt="Key Visual Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 🍀 MODO LUCKY Toggle */}
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center justify-between">
              <Label className="text-lg sm:text-lg font-semibold text-purple-50">🍀 Modo Lucky</Label>
              <button
                onClick={() => {
                  if (!modoLucky) {
                    setModoKV(false) // Desligar modo KV se ativar Lucky
                    setKeyVisualFile(null)
                    setKeyVisualPreview("")
                    setPhotos([])
                    setPhotoPreviews([])
                  } else {
                    // Limpar seleções quando desativar
                    setSelectedVariations([])
                    setAvailableVariations([])
                  }
                  setModoLucky(!modoLucky)
                }}
                className={`w-16 h-8 rounded-full transition-all ${
                  modoLucky ? "bg-purple-600" : "bg-gray-400"
                }`}
              >
                <div className={`w-7 h-7 bg-white rounded-full transition-transform ${
                  modoLucky ? "translate-x-8" : "translate-x-0.5"
                }`} />
              </button>
            </div>
            {modoLucky && (
              <div className="space-y-4 p-5 border-2 rounded-lg bg-purple-900/40 border-purple-500/50 shadow-md shadow-purple-500/20">
                <div className="space-y-2">
                  <Label className="text-base sm:text-base font-medium text-purple-50">
                    Selecione as variações do Lucky para gerar
                  </Label>
                  {loadingVariations ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                      <span className="ml-2 text-purple-200">Carregando variações...</span>
                    </div>
                  ) : availableVariations.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-purple-200">
                          {selectedVariations.length} de {availableVariations.length} variações selecionadas
                        </p>
                        <div className="flex gap-2">
                          {selectedVariations.length < availableVariations.length ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedVariations([...availableVariations])}
                              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-xs"
                            >
                              Selecionar todos
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedVariations([])}
                              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-xs"
                            >
                              Desselecionar todos
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2 border border-purple-500/30 rounded-lg p-3 bg-purple-950/50">
                        {availableVariations.map((variation) => {
                          const isSelected = selectedVariations.includes(variation)
                          return (
                            <label
                              key={variation}
                              className="flex items-center space-x-3 p-2 rounded hover:bg-purple-800/30 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedVariations([...selectedVariations, variation])
                                  } else {
                                    setSelectedVariations(selectedVariations.filter(v => v !== variation))
                                  }
                                }}
                                className="w-4 h-4 text-purple-600 border-purple-500 rounded focus:ring-purple-500 focus:ring-2"
                              />
                              <span className="text-sm text-purple-100 flex-1">
                                {getVariationDisplayName(variation)}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                      {selectedVariations.length === 0 && (
                        <p className="text-sm text-red-400">⚠️ Selecione pelo menos uma variação</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-purple-200">Nenhuma variação disponível</p>
                  )}
                </div>
                <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-3 text-xs text-purple-200">
                  <p className="font-semibold mb-1">💡 Sobre o Modo Lucky:</p>
                  <ul className="space-y-0.5 list-disc list-inside text-xs">
                    <li>Gera múltiplas imagens em paralelo usando variações do mascote Lucky</li>
                    <li>Não é necessário fazer upload de fotos</li>
                    <li>Um ZIP será criado automaticamente com todas as variações geradas</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Lucky Progress Bar */}
          {isLuckyStreaming && (
            <div className="space-y-2 p-4 border-2 rounded-lg bg-purple-900/40 border-purple-500/50">
              <div className="flex justify-between text-sm text-purple-200">
                <span>🍀 Gerando variações...</span>
                <span>{completedVariations}/{totalVariations} ({luckyProgress}%)</span>
              </div>
              <div className="w-full h-3 bg-purple-950 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                  style={{ width: `${luckyProgress}%` }}
                />
              </div>
              {currentVariation && (
                <p className="text-xs text-purple-300 text-center">
                  Processando: {currentVariation.replace('MASCOTE LUCKY - ', '')}
                </p>
              )}
            </div>
          )}

          {/* Generate Button */}
          <div className="relative">
            <style>{`
              @keyframes orbitingGlow {
                0% { background-position: 0% 50%; }
                25% { background-position: 100% 0%; }
                50% { background-position: 100% 100%; }
                75% { background-position: 0% 100%; }
                100% { background-position: 0% 50%; }
              }
              
              .glow-button-wrapper {
                position: relative;
                border-radius: 0.5rem;
              }
              
              .glow-button-wrapper::before {
                content: '';
                position: absolute;
                inset: -3px;
                border-radius: inherit;
                background: linear-gradient(45deg, rgba(168, 85, 247, 0) 0%, rgba(168, 85, 247, 0.6) 20%, rgba(147, 51, 234, 1) 50%, rgba(168, 85, 247, 0.6) 80%, rgba(168, 85, 247, 0) 100%);
                background-size: 200% 200%;
                animation: orbitingGlow 4s ease-in-out infinite;
                pointer-events: none;
                z-index: -1;
                filter: drop-shadow(0 0 20px rgba(168, 85, 247, 0.95));
              }
            `}</style>
            
            <div className="glow-button-wrapper">
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating || isLuckyStreaming}
                className="w-full h-14 sm:h-16 text-base sm:text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-2xl hover:shadow-purple-600/50 transition-all disabled:opacity-50 relative z-10"
                size="lg"
              >
                {isLuckyStreaming ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                    <span className="hidden sm:inline">
                      🍀 {completedVariations}/{totalVariations} variações ({luckyProgress}%)
                    </span>
                    <span className="sm:hidden">
                      🍀 {completedVariations}/{totalVariations}
                    </span>
                  </>
                ) : isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                    <span className="hidden sm:inline">Gerando arte com IA...</span>
                    <span className="sm:hidden">Gerando...</span>
                  </>
                ) : photos.length === 1 && !prompt.trim() && !modoKV ? (
                  "🎨 Gerar Variação"
                ) : modoKV && keyVisualFile && photos.length > 0 && !prompt.trim() ? (
                  "🎨 Gerar com KV"
                ) : modoLucky ? (
                  `🍀 Gerar ${selectedVariations.length} Variações`
                ) : (
                  "Gerar Arte"
                )}
              </Button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-r from-purple-600/20 to-violet-600/20 border border-purple-500/40 rounded-lg p-4 sm:p-5 space-y-3 shadow-lg shadow-purple-500/10 mb-6">
            <div className="flex gap-3 items-start">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-purple-300 flex-shrink-0 mt-1" />
              <div className="text-sm sm:text-base text-purple-50">
                <p className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">💡 Dica:</p>
                <p className="mb-2">Após gerar a arte, você poderá:</p>
                <ul className="list-disc list-inside space-y-1 sm:space-y-2 text-purple-100/90 text-sm sm:text-base">
                  <li>Refazer (gerar variações)</li>
                  <li>Adicionar assets da biblioteca</li>
                  <li>Aplicar templates de composição</li>
                  <li>Exportar como PNG</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}


